import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useOutletContext, useLocation, Outlet } from "react-router-dom";
import { supabase } from "../supabase";
import { formatDateCourt, joursRestants, delaiPalette } from "../helpers";
import Badge from "./Badge";
import BarreFiltres, { appliquerFiltresTri } from "./BarreFiltres";
import Pagination from "./Pagination";
import usePanier from "./usePanier";
import BandeauPanier, { CasePanier } from "./BandeauPanier";
import { RecapImpression } from "./BlocImpression";

export default function ListeCommandes() {
  const { profil, commandes, commandesAJour, setCommandes, chargerMessagesComplets, sousComptes, utilisateursSupervises = [], session } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  // Admin = propriétaire non-dessinateur ; il peut filtrer par utilisateur.
  const isAdmin = profil.role !== "dessinateur" && profil.is_owner === true;
  // Comptes filtrables : tous les utilisateurs pour l'admin, sinon les sous-comptes.
  const comptesFiltrables = isAdmin ? utilisateursSupervises : sousComptes;
  // Filtre par défaut : l'admin se voit lui-même (puis peut switcher).
  const [userFilter, setUserFilter] = useState(isAdmin ? profil.id : null);
  const [menuCmdId, setMenuCmdId] = useState(null);
  const [menuRect, setMenuRect] = useState(null);
  const [showConfirmSupprimer, setShowConfirmSupprimer] = useState(null);
  const [page, setPage] = useState(1);
  // Taille de page par défaut : préférence utilisateur (Réglages), sinon 20.
  const [pageSize, setPageSize] = useState([20, 50, 100].includes(profil?.pref_page_size) ? profil.pref_page_size : 20);
  const navigate = useNavigate();
  const location = useLocation();

  // --- Filters from URL ---
  const filtres = {
    statut:      searchParams.get("statut") || "",
    dessinateur: searchParams.get("dessinateur") || "",
    type:        searchParams.get("type") || "",
    periode:     searchParams.get("periode") || "",
    q:           searchParams.get("q") || "",
    nonlus:      searchParams.get("nonlus") || "",
  };
  // Tri par défaut : délai le plus court d'abord
  const tri = {
    col: searchParams.get("tri") || "delai",
    dir: searchParams.get("dir") || "asc",
  };

  const setFiltres = (next) => {
    const params = new URLSearchParams(searchParams);
    ["statut", "dessinateur", "type", "periode", "q", "nonlus"].forEach(k => {
      if (next[k]) params.set(k, next[k]);
      else params.delete(k);
    });
    setSearchParams(params, { replace: true });
  };

  const setTri = (updaterOrValue) => {
    const nextTri = typeof updaterOrValue === "function" ? updaterOrValue(tri) : updaterOrValue;
    const params = new URLSearchParams(searchParams);
    if (nextTri.col === "delai" && nextTri.dir === "asc") {
      params.delete("tri"); params.delete("dir");
    } else {
      params.set("tri", nextTri.col); params.set("dir", nextTri.dir);
    }
    setSearchParams(params, { replace: true });
  };

  // Toggle helper pour les headers de colonnes cliquables
  const toggleTri = (col) =>
    setTri(prev => prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" });

  // --- Role + accent ---
  const isDessinateur = profil.role === "dessinateur";
  const couleurAccent = isDessinateur ? "#FC6C1B" : "#122131";

  // --- Archive field per role ---
  const champArchive = isDessinateur ? "is_archived_dessinateur" : "is_archived";

  // --- PANIER D'IMPRESSION (branchement ; tout le reste vit dans
  //     usePanier.js et BandeauPanier.js) ---
  //
  // La table de liens EST le verrou (decision D4) : cette seule requete donne
  // donc EXACTEMENT l'ensemble des commandes deja prises dans une impression.
  // Elle est reservee a l'administrateur par la regle RLS.
  //
  // `null` = pas encore lue, ou lecture en echec. Dans les deux cas : AUCUNE
  // case. Mieux vaut pas de panier qu'un panier qui propose une commande deja
  // partie a l'impression.
  const [verrous, setVerrous] = useState(null);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let vivant = true;
    (async () => {
      const { data, error } = await supabase.from("impressions_commandes").select("commande_id");
      if (!vivant) return;
      if (error || !Array.isArray(data)) { setVerrous(null); return; }
      setVerrous(new Set(data.map(l => l && l.commande_id).filter(Boolean)));
    })();
    return () => { vivant = false; };
  }, [isAdmin]);

  // Le panier n'existe que quand les deux sources sont sures : la vraie liste
  // des commandes, et le verrou. Voir le garde-fou explique dans usePanier.js.
  const panierPret = isAdmin && verrous !== null && commandesAJour === true;
  // 🔴 `commandes` ENTIER, pas `actives` : revalider ne peut repondre
  // « archivée » que si la commande figure dans la liste qu'on lui donne. Avec
  // la liste filtree, Simon lirait « n'existe plus ou n'est plus visible » pour
  // une commande simplement archivee — un message faux.
  const panier = usePanier(commandes, verrous, panierPret);
  const auPanier = new Set(panier.ids);

  // Le recapitulatif chiffre du panier. 🔴 Le devis part AU CLIC et nulle part
  // ailleurs (section 4.1 de la spec) : rien n'est precharge pour un panier,
  // puisque la fenetre n'existe que lorsqu'elle est ouverte.
  const [panierOuvert, setPanierOuvert] = useState(null);
  function ouvrirPanier(ids) {
    const choisis = Array.isArray(ids) ? ids : [];
    const parId = new Map(commandes.map(c => [c.id, c]));
    // Les commandes DANS L'ORDRE COCHE, et seulement celles qui existent
    // encore : la porte refuse un identifiant inconnu, et mieux vaut ne pas le
    // lui envoyer.
    const retenues = choisis.map(id => parId.get(id)).filter(Boolean);
    if (retenues.length === 0) return;
    setPanierOuvert(retenues);
  }

  // L'envoi est parti : les commandes du panier sont desormais verrouillees.
  // Elles n'ont donc plus de case a cocher, et les laisser cochees ferait
  // croire qu'on peut recommencer.
  async function panierEnvoye(reponse) {
    if (!reponse || reponse.ok !== true) return;
    panier.vider();
    const { data, error } = await supabase.from("impressions_commandes").select("commande_id");
    if (error || !Array.isArray(data)) return;
    setVerrous(new Set(data.map(l => l && l.commande_id).filter(Boolean)));
  }

  // --- userFilter sub-compte ---
  const commandesVisibles = userFilter
    ? commandes.filter(c => {
        if (profil.role === "dessinateur") return c.dessinateur_id === userFilter;
        return c.utilisateur_id === userFilter;
      })
    : commandes;

  // --- Unread count helper ---
  const auteurNom = `${profil.prenom ?? ""} ${profil.nom ?? ""}`.trim();
  // Notifications uniquement sur SES propres commandes (proprietaire ou
  // dessinateur assigne) — pas en supervision admin.
  const estMaCommande = c => c.utilisateur_id === profil.id || c.dessinateur_id === profil.id;
  const nonLusDe = c => (estMaCommande(c) && c.messages)
    ? c.messages.filter(m => m.auteur !== auteurNom && !(m.lu_par || []).includes(auteurNom)).length
    : 0;

  // Badge notification (chiffre si messages non lus naturels, sinon point orange si manuellement marquée)
  const NotifBadge = ({ c }) => {
    if (!estMaCommande(c)) return null;
    const n = nonLusDe(c);
    if (n > 0) return <span style={{ background: "#FC6C1B", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{n}</span>;
    if (c.marque_non_lu) return <span title="Marquée comme non lue" style={{ display: "inline-block", width: 14, height: 14, borderRadius: "50%", background: "#FC6C1B", flexShrink: 0 }} />;
    return null;
  };

  // Marquage manuel "non lue"
  async function marquerNonLue(commandeId) {
    if (commandesAJour === false) return;
    if (!session?.user?.id) return;
    const { error } = await supabase
      .from("commande_marquage_non_lu")
      .upsert({ commande_id: commandeId, user_id: session.user.id });
    if (!error) {
      setCommandes(prev => prev.map(c => c.id === commandeId ? { ...c, marque_non_lu: true } : c));
    }
  }

  // Marquer en lue : (a) clear le marquage manuel s'il existe + (b) ajouter
  // auteurNom au lu_par de chaque message d'un autre auteur non encore lu.
  async function marquerLue(commandeId) {
    if (commandesAJour === false) return;
    if (!session?.user?.id) return;
    const cmd = commandes.find(x => x.id === commandeId);
    if (!cmd) return;
    const nonLus = (cmd.messages || []).filter(m =>
      m.auteur !== auteurNom && !(m.lu_par || []).includes(auteurNom)
    );
    await Promise.all([
      ...nonLus.map(m =>
        supabase.from("messages").update({ lu_par: [...(m.lu_par || []), auteurNom] }).eq("id", m.id)
      ),
      cmd.marque_non_lu
        ? supabase.from("commande_marquage_non_lu").delete()
            .eq("commande_id", commandeId).eq("user_id", session.user.id)
        : Promise.resolve(),
    ]);
    setCommandes(prev => prev.map(c => {
      if (c.id !== commandeId) return c;
      return {
        ...c,
        marque_non_lu: false,
        messages: (c.messages || []).map(m =>
          nonLus.some(n => n.id === m.id)
            ? { ...m, lu_par: [...(m.lu_par || []), auteurNom] }
            : m
        ),
      };
    }));
  }

  // L'une OU l'autre selon l'état actuel de la commande
  const hasNotif = (c) => estMaCommande(c) && (nonLusDe(c) > 0 || !!c.marque_non_lu);

  // En-tête de colonne cliquable pour tri (desktop tables)
  const Th = ({ col, label }) => (
    <span
      onClick={() => toggleTri(col)}
      style={{ cursor: "pointer", userSelect: "none", color: tri.col === col ? couleurAccent : undefined }}>
      {label}{tri.col === col ? (tri.dir === "asc" ? " ↑" : " ↓") : ""}
    </span>
  );

  // Sélecteur de tri pour le mode cartes (mobile)
  const MobileSort = () => {
    const options = [
      { v: "delai|asc", l: "Délai le plus court" },
      { v: "delai|desc", l: "Délai le plus long" },
      { v: "created_at|desc", l: "Plus récente" },
      { v: "created_at|asc", l: "Plus ancienne" },
      { v: "statut|asc", l: "Statut (A-Z)" },
      { v: "nom_plan|asc", l: "Plan (A-Z)" },
      ...(!isDessinateur ? [{ v: "dessinateur|asc", l: "Dessinateur (A-Z)" }] : []),
    ];
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <select
          value={`${tri.col}|${tri.dir}`}
          onChange={e => { const [col, dir] = e.target.value.split("|"); setTri({ col, dir }); }}
          style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #E5E7EB", fontSize: 12, background: "#fff", color: "#374151", cursor: "pointer" }}>
          {options.map(o => <option key={o.v} value={o.v}>Trier : {o.l}</option>)}
        </select>
      </div>
    );
  };

  // --- Apply filters + sort, puis post-filter notifications ---
  const cmdFiltrees0 = appliquerFiltresTri(commandesVisibles, filtres, tri);
  const cmdFiltrees  = filtres.nonlus ? cmdFiltrees0.filter(c => hasNotif(c)) : cmdFiltrees0;
  const actives   = cmdFiltrees.filter(c => !c[champArchive]);

  // --- Pagination des commandes actives ---
  const pageCount = Math.max(1, Math.ceil(actives.length / pageSize));
  const pageCourante = Math.min(page, pageCount);
  const activesPage = actives.slice((pageCourante - 1) * pageSize, pageCourante * pageSize);
  const setPageSizeReset = (n) => { setPageSize(n); setPage(1); };

  // --- Quick actions: archive/desarchive ---
  async function archiver(id) {
    if (commandesAJour === false) return;
    const { error } = await supabase.from("commandes").update({ is_archived: true }).eq("id", id);
    if (!error) setCommandes(prev => prev.map(c => c.id === id ? { ...c, is_archived: true } : c));
  }
  async function desarchiver(id) {
    if (commandesAJour === false) return;
    const { error } = await supabase.from("commandes").update({ is_archived: false }).eq("id", id);
    if (!error) setCommandes(prev => prev.map(c => c.id === id ? { ...c, is_archived: false } : c));
  }
  // Duplication : ouvre le configurateur pré-rempli avec la commande source
  // pour permettre une modification avant création (cf. NouvelleCommandeModal).
  function dupliquer(c) {
    navigate("/commandes/nouvelle", { state: { dupliquerDe: c } });
  }

  async function supprimerCommande(id) {
    if (commandesAJour === false) return;
    const { error } = await supabase.from("commandes").delete().eq("id", id);
    if (!error) {
      setCommandes(prev => prev.filter(c => c.id !== id));
    } else {
      console.error("Erreur suppression commande:", error);
    }
  }

  async function archiverDessinateur(id) {
    if (commandesAJour === false) return;
    const { error } = await supabase.from("commandes").update({ is_archived_dessinateur: true }).eq("id", id);
    if (!error) setCommandes(prev => prev.map(c => c.id === id ? { ...c, is_archived_dessinateur: true } : c));
  }
  async function desarchiverDessinateur(id) {
    if (commandesAJour === false) return;
    const { error } = await supabase.from("commandes").update({ is_archived_dessinateur: false }).eq("id", id);
    if (!error) setCommandes(prev => prev.map(c => c.id === id ? { ...c, is_archived_dessinateur: false } : c));
  }

  // --- Row click handler ---
  // On propage location.search pour que les filtres et le tri courants soient
  // restaurés quand l'utilisateur ferme le détail (✕ → /commandes?<params>).
  function ouvrirDetail(c) {
    navigate(`/commandes/${encodeURIComponent(c.ref)}${location.search}`);
  }

  // ============================================================
  // UTILISATEUR / ADMIN table rendering
  // ============================================================
  // La colonne du panier n'existe que quand le panier existe : sinon l'en-tete
  // et les lignes n'auraient pas le meme nombre de colonnes, et tout glisserait
  // d'un cran.
  const cmdCols = (panierPret ? "24px " : "") + (sousComptes.length > 0
    ? "1fr 2fr 1fr 1fr 0.6fr 1fr 1.4fr 28px"
    : "2fr 1fr 1fr 0.6fr 1fr 1.4fr 28px");

  function renderLigneCmdUtilisateur(c, dim = false) {
    const owner = sousComptes.find(s => s.id === c.utilisateur_id);
    const ownerLabel = owner
      ? `${owner.prenom} ${owner.nom}`
      : (c.utilisateur_id === profil.id
          ? `${profil.prenom} ${profil.nom}`
          : `${c.client_prenom ?? ""} ${c.client_nom ?? ""}`.trim());
    const j = joursRestants(c.delai);
    const d = delaiPalette(j);
    return (
      <div key={c.id} onClick={() => ouvrirDetail(c)}
        style={{ display: "grid", gridTemplateColumns: cmdCols, padding: "14px 20px", borderBottom: "1px solid #F3F4F6", alignItems: "center", cursor: "pointer", background: "transparent", opacity: dim ? 0.75 : 1, transition: "background 0.1s" }}
        onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
        {/* La cellule est TOUJOURS la quand le panier existe, meme vide : une
            cellule manquante decalerait toute la ligne dans la grille. */}
        {panierPret && (
          <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
            <CasePanier estAdmin={isAdmin} commande={c} verrouillee={verrous.has(c.id)}
              cochee={auPanier.has(c.id)} onBasculer={panier.basculer} />
          </div>
        )}
        {sousComptes.length > 0 && <div style={{ fontSize: 12, color: "#6B7280" }}>{ownerLabel || "—"}</div>}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{c.nom_plan || "—"}</span>
            <NotifBadge c={c} />
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>{ownerLabel ? `${ownerLabel} — ${c.ref}` : c.ref}</div>
        </div>
        <div style={{ fontSize: 12, color: "#6B7280" }}>{c.dessinateur || "—"}</div>
        <div style={{ fontSize: 12, color: "#6B7280" }}>{formatDateCourt(c.created_at)}</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{c.plans?.length ?? 0}</div>
        <div>
          {c.delai ? (
            <>
              <div style={{ fontSize: 12, color: d.text }}>{formatDateCourt(c.delai)}</div>
              {j !== null && <div style={{ fontSize: 10, fontWeight: 600, color: d.accent ? d.text : "#9CA3AF" }}>{j === 0 ? "Aujourd'hui" : j < 0 ? `${Math.abs(j)}j dépassé` : `${j}j restant${j > 1 ? "s" : ""}`}</div>}
            </>
          ) : <span style={{ fontSize: 12, color: "#D1D5DB" }}>—</span>}
        </div>
        <Badge statut={c.statut} />
        <div onClick={e => e.stopPropagation()}>
          <button
            onClick={e => { if (commandesAJour === false) return; const r = e.currentTarget.getBoundingClientRect(); setMenuCmdId(menuCmdId === c.id ? null : c.id); setMenuRect(r); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 18, padding: "2px 4px", lineHeight: 1, borderRadius: 4 }}>
            ···
          </button>
        </div>
      </div>
    );
  }

  function renderCarteCmdUtilisateur(c, dim = false) {
    const j = joursRestants(c.delai);
    const d = delaiPalette(j);
    const owner = sousComptes.find(s => s.id === c.utilisateur_id);
    const ownerLabel = owner ? `${owner.prenom} ${owner.nom}` : null;
    return (
      <div key={c.id} onClick={() => ouvrirDetail(c)}
        style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 10, padding: "12px 14px", marginBottom: 8, cursor: "pointer", opacity: dim ? 0.75 : 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.nom_plan || "—"}</span>
              <NotifBadge c={c} />
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{ownerLabel ? `${ownerLabel} · ` : ""}{c.ref}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* La meme case qu'au bureau : sans elle, le panier serait
                invisible a 375 px (regle dure n. 1). */}
            {panierPret && (
              <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
                <CasePanier estAdmin={isAdmin} commande={c} verrouillee={verrous.has(c.id)}
                  cochee={auPanier.has(c.id)} onBasculer={panier.basculer} />
              </div>
            )}
            <Badge statut={c.statut} />
            <div onClick={e => e.stopPropagation()}>
              <button
                onClick={e => { if (commandesAJour === false) return; const r = e.currentTarget.getBoundingClientRect(); setMenuCmdId(menuCmdId === c.id ? null : c.id); setMenuRect(r); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 18, padding: "2px 4px", lineHeight: 1, borderRadius: 4 }}>
                ···
              </button>
            </div>
          </div>
        </div>
        {c.delai && (
          <div style={{ marginTop: 6, fontSize: 11, color: d.accent ? d.text : "#9CA3AF", fontWeight: d.accent ? 600 : 400 }}>
            {formatDateCourt(c.delai)}{j !== null ? ` · ${j === 0 ? "Aujourd'hui" : j < 0 ? `${Math.abs(j)}j dépassé` : `${j}j restant${j > 1 ? "s" : ""}`}` : ""}
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // DESSINATEUR table rendering
  // ============================================================
  const cmdColsDessinateur = sousComptes.length > 0
    ? "1fr 2fr 1fr 0.6fr 1fr 1.4fr 40px"
    : "2fr 1fr 0.6fr 1fr 1.4fr 40px";

  function renderLigneCmdDessinateur(c, dim = false) {
    const sousD = sousComptes.find(s => s.id === c.dessinateur_id);
    const j = joursRestants(c.delai);
    const d = delaiPalette(j);
    return (
      <div key={c.id} onClick={() => ouvrirDetail(c)}
        style={{ display: "grid", gridTemplateColumns: cmdColsDessinateur, padding: "14px 20px", borderBottom: "1px solid #F3F4F6", alignItems: "center", cursor: "pointer", background: "transparent", opacity: dim ? 0.75 : 1, transition: "background 0.1s" }}
        onMouseEnter={e => e.currentTarget.style.background = "#FFF8F5"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
        {sousComptes.length > 0 && <div style={{ fontSize: 12, color: "#6B7280" }}>{sousD ? `${sousD.prenom} ${sousD.nom}` : "Moi"}</div>}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{c.nom_plan || "—"}</span>
            <NotifBadge c={c} />
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>{c.ref}</div>
        </div>
        <div style={{ fontSize: 12, color: "#6B7280" }}>{formatDateCourt(c.created_at)}</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{c.plans?.length ?? 0}</div>
        <div>
          <div style={{ fontSize: 12, color: d.text }}>{c.delai ? formatDateCourt(c.delai) : "—"}</div>
          {j !== null && <div style={{ fontSize: 10, fontWeight: 600, color: d.accent ? d.text : "#9CA3AF" }}>{j === 0 ? "Aujourd'hui" : j < 0 ? `${Math.abs(j)}j dépassé` : `${j}j restant${j > 1 ? "s" : ""}`}</div>}
        </div>
        <Badge statut={c.statut} />
        <div onClick={e => e.stopPropagation()}>
          <button
            onClick={e => { if (commandesAJour === false) return; const r = e.currentTarget.getBoundingClientRect(); setMenuCmdId(menuCmdId === c.id ? null : c.id); setMenuRect(r); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 18, padding: "2px 4px", lineHeight: 1, borderRadius: 4 }}>
            ···
          </button>
        </div>
      </div>
    );
  }

  function renderCarteCmdDessinateur(c, dim = false) {
    const j = joursRestants(c.delai);
    const d = delaiPalette(j);
    return (
      <div key={c.id} onClick={() => ouvrirDetail(c)}
        style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 10, padding: "12px 14px", marginBottom: 8, cursor: "pointer", opacity: dim ? 0.75 : 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.nom_plan || "—"}</span>
              <NotifBadge c={c} />
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{c.ref}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Badge statut={c.statut} />
            <div onClick={e => e.stopPropagation()}>
              <button
                onClick={e => { if (commandesAJour === false) return; const r = e.currentTarget.getBoundingClientRect(); setMenuCmdId(menuCmdId === c.id ? null : c.id); setMenuRect(r); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 18, padding: "2px 4px", lineHeight: 1, borderRadius: 4 }}>
                ···
              </button>
            </div>
          </div>
        </div>
        {c.delai && (
          <div style={{ marginTop: 6, fontSize: 11, color: d.accent ? d.text : "#9CA3AF", fontWeight: d.accent ? 600 : 400 }}>
            {formatDateCourt(c.delai)}{j !== null ? ` · ${j === 0 ? "Aujourd'hui" : j < 0 ? `${Math.abs(j)}j dépassé` : `${j}j restant${j > 1 ? "s" : ""}`}` : ""}
          </div>
        )}
      </div>
    );
  }

  return (
    <div onClick={() => { menuCmdId && setMenuCmdId(null); }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isDessinateur ? 6 : 6, gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <span>{isDessinateur ? "Missions en cours" : "Commandes en cours"} ({actives.length})</span>
          {(() => {
            const n = commandesVisibles.filter(c => !c[champArchive] && hasNotif(c)).length;
            return n > 0 ? (
              <span title={`${n} commande${n > 1 ? "s" : ""} avec notification`}
                style={{ background: "#FC6C1B", color: "#fff", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 700, lineHeight: 1.5 }}>
                {n}
              </span>
            ) : null;
          })()}
        </h1>
        {!isDessinateur && (
          <button
            onClick={() => navigate("/commandes/nouvelle")}
            style={{ background: "#122131", color: "white", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            + Nouvelle commande
          </button>
        )}
      </div>

      {/* Barre filtres */}
      <BarreFiltres
        commandes={commandes}
        filtres={filtres}
        setFiltres={setFiltres}
        showDessinateur={!isDessinateur}
        couleurAccent={isDessinateur ? "#FC6C1B" : "#122131"}
      />

      {/* Sélecteur sous-compte */}
      {comptesFiltrables.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <select
            value={userFilter ?? ""}
            onChange={e => setUserFilter(e.target.value || null)}
            style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, color: "#374151", background: "#fff", cursor: "pointer" }}>
            <option value="">{isDessinateur ? "Toutes les missions" : isAdmin ? "Tous les utilisateurs" : "Tous les comptes"}</option>
            <option value={profil.id}>{isDessinateur ? "Mes missions" : `${profil.prenom} ${profil.nom} (moi)`}</option>
            {comptesFiltrables.map(s => (
              <option key={s.id} value={s.id}>{s.prenom} {s.nom}</option>
            ))}
          </select>
        </div>
      )}

      {/* ---- UTILISATEUR / ADMIN TABLE ---- */}
      {!isDessinateur && (
        <>
          {/* Le panier : au-dessus du tableau, donc visible au bureau comme au
              telephone. Admin seulement — l'impression l'est aussi. */}
          <BandeauPanier
            estAdmin={panierPret}
            ids={panier.ids}
            commandes={commandes}
            retires={panier.retires}
            onCommander={ouvrirPanier}
            onVider={panier.vider}
            onAcquitter={panier.acquitterRetires}
          />

          {/* Actives — desktop */}
          <div className="cmd-table" style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: cmdCols, padding: "10px 20px", borderBottom: "1px solid #E5E7EB", fontSize: 11, color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {panierPret && <span></span>}
              {sousComptes.length > 0 && <span>Compte</span>}
              <Th col="nom_plan" label="Plan" />
              <Th col="dessinateur" label="Dessinateur" />
              <Th col="created_at" label="Créé le" />
              <span>Plans</span>
              <Th col="delai" label="Délai" />
              <Th col="statut" label="Statut" />
              <span></span>
            </div>
            {actives.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Aucune commande active.</div>}
            {activesPage.map(c => renderLigneCmdUtilisateur(c))}
          </div>

          {/* Actives — mobile */}
          <div className="cmd-cards" style={{ marginBottom: 16 }}>
            <MobileSort />
            {actives.length === 0 && <div style={{ padding: 12, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Aucune commande active.</div>}
            {activesPage.map(c => renderCarteCmdUtilisateur(c))}
          </div>

          <Pagination total={actives.length} page={pageCourante} pageSize={pageSize}
            onPage={setPage} onPageSize={setPageSizeReset} couleur={couleurAccent} />

        </>
      )}

      {/* ---- DESSINATEUR TABLE ---- */}
      {isDessinateur && (
        <>
          {/* Actives — desktop */}
          <div className="cmd-table" style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: cmdColsDessinateur, padding: "10px 20px", borderBottom: "1px solid #E5E7EB", fontSize: 11, color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase" }}>
              {sousComptes.length > 0 && <span>Dessinateur</span>}
              <Th col="nom_plan" label="Plan" />
              <Th col="created_at" label="Créé le" />
              <span>Plans</span>
              <Th col="delai" label="Délai" />
              <Th col="statut" label="Statut" />
              <span></span>
            </div>
            {actives.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Aucune mission active.</div>}
            {activesPage.map(c => renderLigneCmdDessinateur(c))}
          </div>

          {/* Actives — mobile */}
          <div className="cmd-cards" style={{ marginBottom: 16 }}>
            <MobileSort />
            {actives.length === 0 && <div style={{ padding: 12, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Aucune mission active.</div>}
            {activesPage.map(c => renderCarteCmdDessinateur(c))}
          </div>

          <Pagination total={actives.length} page={pageCourante} pageSize={pageSize}
            onPage={setPage} onPageSize={setPageSizeReset} couleur={couleurAccent} />
        </>
      )}

      {/* Outlet — for Task 12 modal. commandesOrdonnees = liste active dans
          l'ordre/filtre affiché, pour la navigation clavier ←/→ du détail. */}
      <Outlet context={{ commandes, commandesAJour, setCommandes, chargerMessagesComplets, sousComptes, profil, session, commandesOrdonnees: activesPage }} />

      {/* ---- DROPDOWN ··· (position fixed, escapes overflow:hidden) ---- */}
      {menuCmdId && menuRect && (() => {
        const c = commandes.find(x => x.id === menuCmdId);
        if (!c) return null;
        const spaceBelow = window.innerHeight - menuRect.bottom;
        const top = spaceBelow >= 220 ? menuRect.bottom + 4 : menuRect.top - 4;
        const transform = spaceBelow >= 220 ? "none" : "translateY(-100%)";
        return (
          <div
            onClick={e => e.stopPropagation()}
            style={{ position: "fixed", top, right: window.innerWidth - menuRect.right, transform, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 1000, minWidth: 210, overflow: "hidden" }}>
            {/* Marquer en non lue / lue — mutuellement exclusifs selon l'état */}
            {hasNotif(c) ? (
              <button
                onClick={() => { setMenuCmdId(null); marquerLue(c.id); }}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151", textAlign: "left" }}
                onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>
                ✓ Marquer en lue
              </button>
            ) : (
              <button
                onClick={() => { setMenuCmdId(null); marquerNonLue(c.id); }}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151", textAlign: "left" }}
                onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>
                🟠 Marquer en non lue
              </button>
            )}
            {isDessinateur ? (
              /* Dessinateur menu: archive / désarchiver uniquement */
              !c.is_archived_dessinateur ? (
                <button
                  onClick={() => { setMenuCmdId(null); archiverDessinateur(c.id); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#DC2626", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>
                  🗃️ Archiver la mission
                </button>
              ) : (
                <button
                  onClick={() => { setMenuCmdId(null); desarchiverDessinateur(c.id); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>
                  📤 Désarchiver la mission
                </button>
              )
            ) : (
              /* Utilisateur / admin menu */
              !c.is_archived ? (
                <>
                  <button
                    onClick={() => { setMenuCmdId(null); navigate(`/commandes/${encodeURIComponent(c.ref)}${location.search}`, { state: { editer: true } }); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151", textAlign: "left" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    ✏️ Voir / modifier
                  </button>
                  <button
                    onClick={() => { setMenuCmdId(null); dupliquer(c); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151", textAlign: "left" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    📋 Dupliquer la commande
                  </button>
                  <button
                    onClick={() => { setMenuCmdId(null); archiver(c.id); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#DC2626", textAlign: "left" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    🗃️ Archiver la commande
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setMenuCmdId(null); desarchiver(c.id); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151", textAlign: "left" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    📤 Désarchiver la commande
                  </button>
                  <button
                    onClick={() => { setMenuCmdId(null); setShowConfirmSupprimer(c.id); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#DC2626", textAlign: "left" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    🗑️ Supprimer la commande
                  </button>
                </>
              )
            )}
          </div>
        );
      })()}

      {/* Modal confirmation suppression */}
      {showConfirmSupprimer && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 400, boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#122131", marginBottom: 12 }}>Supprimer la commande ?</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 24, lineHeight: 1.6 }}>
              Cette action est irréversible. La commande et toutes ses données associées (messages, versions, fichiers) seront définitivement supprimées.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowConfirmSupprimer(null)}
                style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #D1D5DB", background: "#F9FAFB", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Annuler
              </button>
              <button onClick={() => { supprimerCommande(showConfirmSupprimer); setShowConfirmSupprimer(null); }}
                style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: "#DC2626", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Le recapitulatif du panier : rendu UNE SEULE FOIS, et seulement quand
          il est ouvert. C'est son montage qui demande le devis. */}
      {panierOuvert && (
        <RecapImpression
          commandes={panierOuvert}
          nbCommandes={panierOuvert.length}
          onFermer={() => setPanierOuvert(null)}
          onImpressionMaj={panierEnvoye}
        />
      )}
    </div>
  );
}
